import { useQueryClient } from "@tanstack/react-query";

import { getErrorMessage } from "@vega-api/custom/utils/errorHandling";
import {
  useGetInsightsLimitedPartnerVisibility,
  useUpdateInsightsLimitedPartnerVisibility,
} from "@vega-api/generated/insights/insights";
import { type LimitedPartnerInsightsVisibilityField } from "@vega-api/generated/types/limitedPartnerInsightsVisibilityField";
import { useToast } from "@vega-core/components/Toast/useToast";
import { m } from "@vega-core/paraglide/messages";

export const useLpVisibility = (limitedPartnerId: string) => {
  const queryClient = useQueryClient();
  const showToast = useToast();

  const { data, isPending, isError, refetch, queryKey } =
    useGetInsightsLimitedPartnerVisibility(limitedPartnerId, {
      query: { enabled: Boolean(limitedPartnerId) },
    });

  const updateMutation = useUpdateInsightsLimitedPartnerVisibility({
    mutation: {
      onSuccess: (response) => {
        // PUT returns the updated config — seed the cache directly, no refetch
        queryClient.setQueryData(queryKey, response);
        showToast({
          variant: "success",
          title: m.coInvestments_insights_lpVisibility_saveSuccess(),
        });
      },
      onError: (error) => {
        const { title, description } = getErrorMessage(error, {
          defaultTitle: m.coInvestments_insights_lpVisibility_saveError(),
        });
        showToast({ variant: "destructive", title, description });
      },
    },
  });

  const save = (fields: LimitedPartnerInsightsVisibilityField[]) => {
    updateMutation.mutate({
      limitedPartnerId,
      data: {
        fields: fields.map(({ key, isVisible }) => ({ key, isVisible })),
      },
    });
  };

  return {
    fields: data?.data.fields ?? [],
    isLoading: isPending,
    isError,
    refetch,
    save,
    isSaving: updateMutation.isPending,
  };
};
